import GooglePlayStoreLink from "@ui-application/navigation/GooglePlayStoreLink/GooglePlayStoreLink.web";
import AppleAppStoreLink from "@ui-application/navigation/AppleAppStoreLink/AppleAppStoreLink.web";
import PageMaxWidth from "@ui-kit/layout/PageMaxWidth/PageMaxWidth.web";
import {
  FOOTER_DATA,
  SIMPLE_FOOTER_DATA,
} from "@shared/constants/footer/footer-data";
import Accordion from "@ui-kit/disclosure/Accordion/Accordion.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import Link from "@ui-kit/navigation/Link/Link.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";
import { useRouter } from "next/router";
import { useCallback } from "react";
import WebviewComponentAdaptor from "@shared/components/application/other/WebviewComponentAdaptor/WebviewComponentAdaptor.web";
import NewsletterSignup from "../NewsletterSignup/NewsletterSignup.web";

const Footer = () => {
  const { t } = useTranslation("common");
  const { links, socialMedia } = FOOTER_DATA;
  const { links: simpleFooterLinks } = SIMPLE_FOOTER_DATA;
  const router = useRouter();

  /**
   * TODO: use props not hardcoded paths
   */
  const simplifiedFooterLinks = [
    "/[eventSlug]",
    "/e/[eventSlug]",
    "/event/[eventSlug]",
    "/m/[membershipSlug]",
    "/pass/[membershipSlug]",
    "/purchase-dynamic",
  ];
  const shouldShowSimpleFooter = simplifiedFooterLinks.includes(
    router.pathname
  );

  const getSocialIcon = (name: string) => {
    // Use semantic token for simple footer to support dark mode
    const color = shouldShowSimpleFooter ? "fg.muted" : "white"; // footer social icons are white regardless
    const iconSize = shouldShowSimpleFooter ? "20px" : "15px";

    switch (name) {
      case "Facebook":
        return (
          <Icon.FacebookLogo color={color} width={iconSize} height={iconSize} />
        );
      case "Twitter":
        return <Icon.XLogo color={color} width={iconSize} height={iconSize} />;
      case "LinkedIn":
        return (
          <Icon.LinkedInLogo color={color} width={iconSize} height={iconSize} />
        );
      case "Instagram":
        return (
          <Icon.InstagramLogo
            color={color}
            width={iconSize}
            height={iconSize}
          />
        );
      default:
        return null;
    }
  };

  const getLinkComponent = useCallback(
    (name: string, path: string) => {
      return (
        <Box mb={shouldShowSimpleFooter ? 0 : 4} key={name}>
          <Link
            href={path}
            color={shouldShowSimpleFooter ? "fg.muted" : "whiteAlpha.700"}
            fontSize="sm"
            _hover={{
              color: shouldShowSimpleFooter ? "primary.fg" : "whiteAlpha.900",
            }}
          >
            {t(name)}
          </Link>
        </Box>
      );
    },
    [shouldShowSimpleFooter, t]
  );

  return (
    <WebviewComponentAdaptor
      webComponent={
        <Box
          p={shouldShowSimpleFooter ? "24px 0" : "32px 0"}
          px="none"
          bg={shouldShowSimpleFooter ? "transparent" : "primary.bg"}
          width="100%"
          borderTop={shouldShowSimpleFooter ? "1px solid" : "none"}
          borderTopColor={shouldShowSimpleFooter ? "border" : undefined}
        >
          <PageMaxWidth>
            {shouldShowSimpleFooter ? (
              <Flex
                width="100%"
                color="fg.muted"
                flexDirection={{ base: "column", md: "row" }}
                alignItems="center"
                fontSize="0.875rem"
                lineHeight="150%"
                gap="md"
              >
                <Flex justifySelf={{ base: "center", md: "flex-start" }}>
                  <Text
                    pr="xl"
                    borderRight="1px solid"
                    borderRightColor="border"
                  >
                    Showpass &copy; {new Date().getFullYear()}
                  </Text>
                  <Flex gap="4xl" ml="xl">
                    {simpleFooterLinks.map((link) => {
                      const { name, path } = link;
                      return getLinkComponent(name, path);
                    })}
                  </Flex>
                </Flex>
                <Flex ml={{ md: "auto" }} gap="xl">
                  {socialMedia &&
                    socialMedia.map((social) => {
                      return (
                        <Link
                          href={social.link}
                          key={social.name}
                          aria-label={`Link for ${social.name}`}
                          external
                          target="_blank"
                        >
                          {getSocialIcon(social.name)}
                        </Link>
                      );
                    })}
                </Flex>
              </Flex>
            ) : (
              <Box
                display={{ sm: "grid" }}
                gridTemplateRows={{ sm: "repeat(3, auto)" }}
                gridTemplateColumns={{ sm: "repeat(4, auto)" }}
              >
                <Box
                  display="none"
                  pb={{ base: "2xl", sm: "none" }}
                  gridColumn={{ sm: "3/5" }}
                >
                  <NewsletterSignup
                    title={t(
                      "Subscribe to Showpass to receive the latest news"
                    )}
                    placeholder={t("Enter your email")}
                    buttonText={t("Subscribe")}
                  />
                </Box>
                {links && (
                  <Box gridColumn="1/5">
                    <Accordion
                      collapsible
                      display={{ base: "block", sm: "none" }}
                      collapseOverflowVisible
                    >
                      {links.map((category, index) => {
                        return (
                          <Accordion.Item
                            key={category.id}
                            borderColor="primary.700"
                            borderLeft="none"
                            borderRight="none"
                            borderRadius="none"
                            margin="none"
                            marginTop="none"
                            borderTop={index === 0 ? "none" : undefined}
                          >
                            <h2>
                              <Accordion.Trigger
                                color="bg"
                                backgroundColor="transparent"
                                borderRadius="none"
                                padding="20px 0"
                                _hover={{ backgroundColor: "transparent" }}
                                _focus={{ boxShadow: "none" }}
                              >
                                <Box flex="1" textAlign="left">
                                  <Text
                                    as="p"
                                    color="bg"
                                    fontWeight="700"
                                    fontSize={{ base: "md", sm: "sm" }}
                                    lineHeight={{ base: "19.2px", sm: "26px" }}
                                    pb={{ sm: "md" }}
                                  >
                                    {category.name}
                                  </Text>
                                </Box>
                              </Accordion.Trigger>
                            </h2>
                            <Accordion.Content pl={0}>
                              {category.footer_links.map((link) => {
                                const { name, path } = link;
                                return getLinkComponent(name, path);
                              })}
                            </Accordion.Content>
                          </Accordion.Item>
                        );
                      })}
                    </Accordion>
                    <Box
                      display={{ base: "none", sm: "flex" }}
                      mt={{ sm: "3xl" }}
                      mb={{ sm: "2xl" }}
                      justifyContent={{ sm: "space-between" }}
                    >
                      {links.map((category) => {
                        return (
                          <Box key={category.id}>
                            <Text
                              as="p"
                              color="bg"
                              fontWeight="700"
                              fontSize={{ base: "md", sm: "sm" }}
                              lineHeight={{ base: "19.2px", sm: "26px" }}
                              pb={{ sm: "md" }}
                            >
                              {category.name}
                            </Text>
                            {category.footer_links.map((link) => {
                              const { name, path } = link;
                              return getLinkComponent(name, path);
                            })}
                          </Box>
                        );
                      })}
                      <Box>
                        <Text
                          as="p"
                          color="bg"
                          mb="lg"
                          fontWeight="700"
                          fontSize="sm"
                          lineHeight="26px"
                        >
                          {t("Download the App")}
                        </Text>
                        <Flex flexDirection="column" gap="lg">
                          <AppleAppStoreLink
                            width="element.7xl"
                            height="element.2xl"
                          />
                          <GooglePlayStoreLink
                            width="element.7xl"
                            height="element.2xl"
                          />
                        </Flex>
                      </Box>
                    </Box>
                  </Box>
                )}
                <Flex
                  my="xl"
                  alignItems="center"
                  justifyContent="center"
                  flexDirection="column"
                  display={{ base: "flex", sm: "none" }}
                >
                  <Text
                    as="p"
                    color="bg"
                    mb="lg"
                    fontWeight="700"
                    fontSize="sm"
                    lineHeight="1.2"
                  >
                    {t("Download the App")}
                  </Text>
                  <Flex direction="row" gap="lg">
                    <AppleAppStoreLink
                      width="element.8xl"
                      height="element.3xl"
                    />
                    <GooglePlayStoreLink
                      width="element.8xl"
                      height="element.3xl"
                    />
                  </Flex>
                </Flex>
                {socialMedia && (
                  <Flex
                    maxWidth="element.8xl"
                    justifyContent="space-between"
                    alignItems="center"
                    m={{ base: "0 auto 18px", sm: "unset" }}
                    gridColumn={{ sm: "1/2" }}
                    gridRow={{ sm: "1/1" }}
                  >
                    {socialMedia.map((social) => {
                      return (
                        <Link
                          href={social.link}
                          key={social.name}
                          aria-label={`Link for ${social.name}`}
                        >
                          {getSocialIcon(social.name)}
                        </Link>
                      );
                    })}
                  </Flex>
                )}
                <Text
                  as="p"
                  textAlign="center"
                  color="bg"
                  fontSize="xs"
                  lineHeight="26px"
                  gridColumn={{ sm: "1/5" }}
                >
                  Showpass &copy; {new Date().getFullYear()}
                </Text>
              </Box>
            )}
          </PageMaxWidth>
        </Box>
      }
      webviewComponent={null}
    />
  );
};

export default Footer;
